import {
  RetweetOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Empty, Flex } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { useInstitutionServiceGetInstitutionHierarchyEndpoint } from 'src/api/queries'
import {
  GetInstitutionHierarchyResponse,
  InstitutionService
} from 'src/api/requests'

import { OrganizationalChart } from '../Organiogram'
import { OrganizationalHeader } from '../OrganizationalHeader'

import { useStyles } from './styles'

const normalizeData = (
  node: GetInstitutionHierarchyResponse
): GetInstitutionHierarchyResponse => ({
  ...node,
  children: node?.children?.length ? node?.children?.map(normalizeData) : null
})

const DRAGGABLE_CONTAINER_ID = 'draggable-container'

const initialPosition = { x: 0, y: 0 }
const initialScale = 0.8

export type Filters = Record<string, string>

export const OrganizationalOrganogram = () => {
  const [filters, setFilters] = useState<Filters>({})

  const [treeData, setTreeData] = useState<GetInstitutionHierarchyResponse>()
  const [position, setPosition] = useState(initialPosition)
  const [dragging, setDragging] = useState(false)
  const [startPosition, setStartPosition] = useState(initialPosition)
  const [scale, setScale] = useState(initialScale)

  const zoomIn = () => setScale((prevScale) => Math.min(prevScale + 0.1, 2))
  const zoomOut = () => setScale((prevScale) => Math.max(prevScale - 0.1, 0.5))

  const queryClient = useQueryClient()
  const { styles } = useStyles({
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    transformOrigin: '0 0',
    dragging
  })

  const queryArgs =
    Object.keys(filters).length === 0
      ? { ...filters, maxDepth: 1 }
      : { ...filters }

  const { data, isLoading, isError } =
    useInstitutionServiceGetInstitutionHierarchyEndpoint(queryArgs)

  const normalizedData = normalizeData(data as GetInstitutionHierarchyResponse)

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setDragging(true)
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    },
    [position]
  )

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (dragging) {
        setPosition({
          x: e.clientX - startPosition.x,
          y: e.clientY - startPosition.y
        })
      }
    },
    [dragging, startPosition]
  )

  const onMouseUp = useCallback(() => {
    setDragging(false)
  }, [])

  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault()
    if (e.deltaY < 0) {
      zoomIn()
    } else {
      zoomOut()
    }
  }, [])

  useEffect(() => {
    const container = document.getElementById(DRAGGABLE_CONTAINER_ID)
    if (container) {
      container.addEventListener('wheel', onWheel)
    }
    return () => {
      if (container) {
        container.removeEventListener('wheel', onWheel)
      }
    }
  }, [onWheel])

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    } else {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [dragging, onMouseMove, onMouseUp])

  const resetZoomAndPosition = () => {
    setPosition(initialPosition)
    setScale(initialScale)
  }

  const findAndSetChildren = useCallback(
    (
      node: GetInstitutionHierarchyResponse,
      parentId: string,
      children: GetInstitutionHierarchyResponse[]
    ): boolean => {
      if (node.id === parentId) {
        node.children = children
        return true
      }
      if (!node.children) return false
      for (const c of node.children) {
        if (findAndSetChildren(c, parentId, children)) return true
      }
      return false
    },
    []
  )

  const onAddChild = useCallback(
    async (parentId: string) => {
      const subtree = await queryClient.fetchQuery({
        queryKey: ['institutionHierarchy', { parentId }],
        queryFn: () =>
          InstitutionService.getInstitutionHierarchyEndpoint(
            parentId,
            undefined,
            undefined,
            undefined,
            undefined,
            1
          )
      })

      setTreeData((prev) => {
        if (!prev) return prev

        const newTree = structuredClone(prev)

        const normalizedBranch = normalizeData(subtree)

        const injected = findAndSetChildren(
          newTree,
          parentId,
          normalizedBranch.children || []
        )
        if (!injected) {
          console.warn(`Parent node ${parentId} not found in tree!`)
        }

        return newTree
      })
    },
    [queryClient, findAndSetChildren]
  )

  useEffect(() => {
    if (data) setTreeData(normalizedData)
  }, [data])

  return (
    <>
      <OrganizationalHeader setFilters={setFilters} />

      <Flex gap={8}>
        <Button onClick={zoomIn} icon={<ZoomInOutlined />} />
        <Button onClick={zoomOut} icon={<ZoomOutOutlined />} />
        <Button onClick={resetZoomAndPosition} icon={<RetweetOutlined />} />
      </Flex>

      <div className={styles.wrapper}>
        <Card className={styles.card} loading={isLoading && !isError}>
          <div
            onMouseDown={onMouseDown}
            id={DRAGGABLE_CONTAINER_ID}
            className={styles.chartContainer}
          >
            {data && treeData ? (
              <div className={styles.chart}>
                <OrganizationalChart
                  data={treeData as GetInstitutionHierarchyResponse}
                  onAddChild={onAddChild}
                />
              </div>
            ) : (
              <Empty />
            )}
          </div>
        </Card>
      </div>
    </>
  )
}
